import type { FlowEditorNode, FlowEditorState, FlowNodeKind, WidgetConfigState, WidgetTab } from "./types";
import { DonateWidget, DonateWidgetPayload, DonateWidgetType } from "services/main/donate.service";
import { RGL_COLS, RGL_CONTAINER_PADDING, RGL_MARGIN, RGL_ROW_HEIGHT, WIDGET_DEFINITIONS, viewportPresets } from "./constants";
import { getAlertLayoutPresetDefinition } from "./constants";
import { getFlowViewport, getGridConfig, getGridRows } from "./flowViewportUtils";
import { getNodeGridItem } from "./flowGridUtils";
import { getEffectiveNodeAnchor } from "./flowAnchorUtils";
import { constrainFlowStateToEnabledObjects, createDefaultFlowState, isEnabledFlowNode } from "./flowNodeStateUtils";
import { buildFrontendPlacement, getDonateWidgetTypeFromKind } from "./flowWidgetKindUtils";
import { isEnabledWidgetTab } from "./widgetUtils";
export const getWidgetTypeFromFlow = (
  flowState: FlowEditorState | undefined,
  fallback: DonateWidgetType,
): DonateWidgetType => {
  const firstWidgetKind = flowState?.nodes.find(isEnabledFlowNode)?.data.kind;

  if (firstWidgetKind === "alert") return "DONATION_ALERT";
  if (firstWidgetKind === "goal") return "DONATION_GOAL";
  if (firstWidgetKind === "leaderboard") return "DONATION_LIST";
  if (firstWidgetKind === "timer" || firstWidgetKind === "chat")
    return "CUSTOM";
  return fallback;
};

export const buildGridItemData = (node: FlowEditorNode) => {
  const kind = node.data.kind || "text";
  const settings = node.data.settings || {};
  const data: Record<string, any> = {
    kind,
    text: node.data.text || "",
  };

  if (kind === "alert") {
    data.template = settings.messageTemplate || node.data.text || "";
    data.imageUrl = settings.iconUrl || settings.imageUrl || node.data.imageUrl;
  }

  if (kind === "image" && (settings.imageUrl || node.data.imageUrl)) {
    data.imageUrl = settings.imageUrl || node.data.imageUrl;
  }

  return data;
};

export const buildWidgetGridLayout = (
  flowState?: FlowEditorState,
): DonateWidgetPayload["gridLayout"] => {
  const nodes = (flowState?.nodes || []).filter(isEnabledFlowNode);
  const viewport = flowState?.viewport
    ? getFlowViewport(flowState.viewport)
    : getFlowViewport();
  const layouts = viewportPresets.reduce(
    (nextLayouts, preset) => {
      const presetViewport = getFlowViewport({ device: preset.key });

      return {
        ...nextLayouts,
        [preset.key]: nodes.map((node) => {
          const item = getNodeGridItem(node, presetViewport);

          return {
            i: node.id,
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
          };
        }),
      };
    },
    {} as NonNullable<DonateWidgetPayload["gridLayout"]>["layouts"],
  );

  return {
    layouts,
    items: nodes.map((node) => {
      const kind = node.data.kind || node.type;
      const alertPreset =
        kind === "alert"
          ? getAlertLayoutPresetDefinition(
              node.data.settings?.layoutPreset || node.data.settings?.layout,
              node.data.settings?.theme,
            )
          : undefined;

      return {
        id: node.id,
        type: getDonateWidgetTypeFromKind(kind),
        title: node.data.label || "Widget",
        isVisible: true,
        data: buildGridItemData(node),
        settings: {
          ...(node.data.settings || {}),
          ...(kind === "alert"
            ? {
                layoutPreset: alertPreset?.key,
                theme: alertPreset?.theme,
                backgroundStyle: "none",
                effect: "none",
                iconMode: "image",
                showIcon: node.data.settings?.showIcon ?? alertPreset?.showIcon,
              }
            : {}),
          anchor: getEffectiveNodeAnchor(node, viewport),
          frontendPlacement: buildFrontendPlacement(node, viewport),
        },
        styles: {
          color: node.data.color,
        },
      };
    }),
    breakpoints: {
      lg: 1200,
      md: 996,
      sm: 768,
      xs: 480,
      xxs: 0,
    },
    cols: RGL_COLS,
    rowHeight: RGL_ROW_HEIGHT,
    margin: RGL_MARGIN,
    containerPadding: RGL_CONTAINER_PADDING,
    compactType: "vertical",
    preventCollision: false,
    isDraggable: false,
    isResizable: false,
  };
};

export const buildWidgetPayload = (
  tab: WidgetTab,
  config: WidgetConfigState,
  baseWidget?: DonateWidget,
  flowState?: FlowEditorState,
): DonateWidgetPayload => {
  const enabledTab = isEnabledWidgetTab(tab) ? tab : "alert";
  const definition = WIDGET_DEFINITIONS[enabledTab];
  const baseFlowState = flowState || createDefaultFlowState(enabledTab, config);
  const nextFlowState = constrainFlowStateToEnabledObjects(
    baseFlowState,
    enabledTab,
    config,
  );
  const widgetType = getWidgetTypeFromFlow(nextFlowState, definition.type);
  const isSameType = baseWidget?.type === widgetType;

  return {
    name: baseWidget?.name || definition.name,
    type: widgetType,
    description: baseWidget?.description || definition.description,
    isPublic: baseWidget?.isPublic ?? true,
    isDefault: isSameType
      ? (baseWidget?.isDefault ?? widgetType !== "CUSTOM")
      : widgetType !== "CUSTOM",
    status: baseWidget?.status || "ACTIVE",
    gridLayout: buildWidgetGridLayout(nextFlowState),
    settings: {},
    styles: {},
    metadata: {
      ...(baseWidget?.metadata || {}),
      version: 1,
      widgetKey: enabledTab,
      layoutEngine: "frontend-responsive",
      responsive: "frontend",
      nodeCount: nextFlowState.nodes.length,
      source: "donate-portal",
    },
  };
};
